import { markRaw, useContext } from '@nuxtjs/composition-api';
import { GenericObjectType } from '~/utils/types';
import { getCustomProps } from './CmsComponentProps';
import { getClasses, getPageBuilderStyleNode, getStyles } from './CmsComponentStylings';
import { ALLOWED_COMPONENTS } from './CmsComponentImport';
import { cleanButtonsClasses, parseClasses, replaceStaticWithCdnInCss, replaceStaticWithCdnInImages } from './functions';

const parseMediaQueries = (cssClasses: Array<string>) => {
  return cssClasses.map(el => parseClasses(el))
}

/**
 * Retrieve the CSS classes from a content type node
 * @param node
 * @returns {{cssClasses: any}}
 */
export function getCssClasses(node, dataset) {
  return {
    cssClasses: parseMediaQueries([...getClasses(node, dataset), ...node.getAttribute('class')
      ? node.getAttribute('class').split(' ')
      : []]),
  };
}

/**
 * Retrieve if CSS display property is set to none from a content type node
 *
 * @param node
 * @returns {{isHidden: boolean}}
 */
export function getIsHidden(node) {
  return {
    isHidden: node.style.display === 'none',
  };
}

/**
 * Converts a CSS string style into a JSX object inline style
 *
 * @param {String} style
 * @returns {Object}
 */
export function cssToJSXStyle(style) {
  const toCamelCase = (str) => str.replace(/-(.)/g, (_, p) => p.toUpperCase());
  const result = {};
  style.split(';').forEach((el) => {
    const [prop, value] = el.split(':');
    if (prop) {
      result[toCamelCase(prop.trim())] = value.trim();
    }
  });

  return result;
}

/**
 * Retrieve media queries from a master format node
 *
 * @param dataset  - The node dataset extended
 * @param {Array} mediaQueries
 *
 * @returns {{mediaQueries: {media: string, style: string}}}
 */
export function getMediaQueries(dataset: Object) {
  const response = [];

  const medias = Object.keys(dataset)
    .filter((key) => key.match(/media-/))
    .map((key) => dataset[key]);

  const styles = Object.keys(dataset)
    .filter((key) => key.match(/mediaStyle/))
    .map((key) => dataset[key]);

  medias.forEach((media, i) => {
    response.push({
      media,
      style: cssToJSXStyle(styles[i]),
    });
  });

  return { mediaQueries: response };
}

//@TODO centralizzare
const IMAGE_STATIC_REPLACEMENT = 'ecommerce-static';
const IMAGE_CDN_REPLACEMENT = process.env.SOLE_ENV == 'prod' ? 'ecommerce-cdn' : 'ecommerce-static';

export const getAsJSON = (nodeParam, avoidParse = false, isDev, extractBody = false, cmsUrl, printTypes = false) => {
  let node = nodeParam;
  if (!avoidParse) {
    if (typeof node === 'string') {
      node = node.replace(/target=/g, 'data-target=') /** $dompurify removes target="_blank" so we do this replace and ther reinsert the target in PageBuilderComponent */
      node = replaceStaticWithCdnInCss('background-image', node, IMAGE_STATIC_REPLACEMENT, IMAGE_CDN_REPLACEMENT);
      node = replaceStaticWithCdnInImages(node, IMAGE_STATIC_REPLACEMENT, IMAGE_CDN_REPLACEMENT);
      // @TODO fix on magento. When applying template on pdp pagebuilder fields, the images src will be like {{media url=wysiwyg/image.png}} instead of the full image path
      try {
        const baseUrl = cmsUrl || '';
        node = node.replace(/{{media url=/g, baseUrl + '/media/').replace(/}}/g, '')
      } catch (e) { console.warn('CATCH error replacing cmsUrl', e) }
    }
    node = parseClasses(node)

    if (process.server) {
      const { JSDOM } = require('jsdom');
      const dom = new JSDOM(typeof node === 'string' ? node : '');
      node = dom.window.document;
    } else {
      node = new DOMParser().parseFromString(node as string, 'text/html');
    }

    if (node.body && extractBody) node = node.body.firstChild
  } else {
  }
  if (node.dataset?.contentType == 'block') {
    try {
      const innerHTML = node.innerHTML || '';
      const blockIdAttr = innerHTML.split(' ').find(el => el.includes("block_id"));
      if (blockIdAttr) {
        node.dataset.blockId = blockIdAttr.split('=')[1].replaceAll('"', '');
      }
    } catch (e) { console.warn("Impossibile estrarre blockId", e) }
  }

  const content = node.body ?? node;
  content.id = `id-${Date.now().toString()}`;
  let children = [...content.querySelectorAll(`#${content.id} > [data-content-type]`)];
  /** check second level to bypass wrappers */
  if (children.length === 0) {
    children = [...content.querySelectorAll(`#${content.id} > * > [data-content-type]`)];
  }


  let dataset = { ...node.dataset } as GenericObjectType;
  dataset = { ...dataset, ...getCustomProps(node, content, { IMAGE_STATIC_REPLACEMENT, IMAGE_CDN_REPLACEMENT }) };
  if (dataset.contentType == 'buttons') {
    children.forEach((child, i) => {
      const classes = child?.classList ?? [];
      Array.from(classes).forEach((c: string) => {
        if (c.includes('btnColor-') || c.includes('btnText-')) {
          children[i].classList.remove(c)
          let btn = child?.querySelector?.('[class*=pagebuilder-button]')
          btn?.classList.add(c)
          btn = cleanButtonsClasses(btn);
        }
      })

    })
  }

  const DOMContent = (avoidParse)
    || (node.dataset && !Object.keys(ALLOWED_COMPONENTS).includes(node.dataset.contentType as string))
    ? node : null;
  printTypes && dataset.contentType == 'row' && console.log({
    datasetShowOnline: dataset.showOnline, showOnlineConditionAsTrue: dataset.showOnline == 'true', showOnlineConditionAsUndefined: dataset.showOnline == undefined, showOnlineCondition: !!(dataset.showOnline == undefined || dataset.showOnline == 'true')
  })
  const object = {
    pageBuilderStyleNode: getPageBuilderStyleNode(node, avoidParse),
    tagname: node.tagName,
    children: children.map((el) => getAsJSON(el, true, isDev, false, cmsUrl, printTypes)),
    ...dataset,
    showInPreview: !!(dataset.showInPreview == undefined || dataset.showInPreview == 'true'),//managing undefined as default true
    showOnline: !!(dataset.showOnline == undefined || dataset.showOnline == 'true'),//managing undefined as default true
    target: node.target,
    styles: getStyles(node),
    ...getCssClasses(content, dataset),
    DOMContent,
    ...getMediaQueries(dataset)
  };
  return markRaw(object);
};
